import { useState } from 'react';
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Button,
  Typography,
} from '@mui/material';
import {
  BuildOutlined,
  DeleteOutlined,
  HealingOutlined,
  PauseOutlined,
  PlayArrowOutlined,
  ReplayOutlined,
  RestartAltOutlined,
  VpnKeyOutlined,
} from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import { useNotifications } from '@/hooks/use-notifications';
import {
  useDeleteProcessorMutation,
  useProcessorMaintenanceMutation,
  useProcessorPauseMutation,
  useProcessorResumeMutation,
} from '@/state/mutations/processor-admin/use-processor-lifecycle-mutations';
import { useReleaseProcessorLeaseMutation } from '@/state/mutations/processor-admin/use-release-processor-lease';
import { useResetProcessorMutation } from '@/state/mutations/processor-admin/use-reset-processor';
import {
  useRecoverProcessorMutation,
  useReissueProcessorTokenMutation,
} from '@/state/mutations/processor-admin/use-processor-recovery-mutations';
import {
  isProcessorHealStuck,
  resolveProcessorAutomationCaption,
  type ProcessorAutomationSeverity,
} from '@/helpers/processor-automation.helper';
import type { ProcessorPoolMemberDto } from '@/types/dtos/processor/processor-pool.dto';
import { VmFontPackIndicator } from '@/components/common/vm-font-pack-indicator';

const AUTOMATION_CAPTION_COLOR: Record<ProcessorAutomationSeverity, string> = {
  info: 'text.secondary',
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
};

function vmDisplayStatus(vm: ProcessorPoolMemberDto['vms'][number]): string | undefined {
  if (vm.activationStatus === 'deployed' && vm.vmStatus) {
    return vm.vmStatus;
  }
  if (vm.activationStatus && vm.vmStatus && vm.activationStatus.toLowerCase() === vm.vmStatus.toLowerCase()) {
    return vm.activationStatus;
  }
  if (vm.activationStatus && vm.vmStatus) {
    return `${vm.activationStatus} · ${vm.vmStatus}`;
  }
  return vm.activationStatus ?? vm.vmStatus;
}

export function ProcessorVmCell({ processor }: { processor: ProcessorPoolMemberDto }) {
  if (!processor.vms?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <>
      {processor.vms.map((vm) => {
        const status = vmDisplayStatus(vm);
        return (
          <Stack key={vm.vmId} direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Tooltip title={vm.vmId}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                {vm.vmId.length > 22 ? `${vm.vmId.slice(0, 22)}…` : vm.vmId}
              </Typography>
            </Tooltip>
            <VmFontPackIndicator fontPack={vm.fontPack} />
            {status && <Chip size="small" label={status} variant="outlined" />}
          </Stack>
        );
      })}
    </>
  );
}

type ConfirmAction = 'maintenance' | 'pause' | 'release' | 'reset' | 'delete';

export default function ProcessorLifecycleActions({ processor }: { processor: ProcessorPoolMemberDto }) {
  const t = useTranslations();
  const notification = useNotifications();
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const { mutateAsync: enterMaintenance, isPending: isMaintenance } = useProcessorMaintenanceMutation();
  const { mutateAsync: pause, isPending: isPausing } = useProcessorPauseMutation();
  const { mutateAsync: resume, isPending: isResuming } = useProcessorResumeMutation();
  const { mutateAsync: deleteProcessor, isPending: isDeleting } = useDeleteProcessorMutation();
  const { mutateAsync: releaseLease, isPending: isReleasing } = useReleaseProcessorLeaseMutation();
  const { mutateAsync: resetProcessor, isPending: isResetting } = useResetProcessorMutation();
  const { mutateAsync: reissueToken, isPending: isReissuing } = useReissueProcessorTokenMutation();
  const { mutateAsync: recoverProcessor, isPending: isRecovering } = useRecoverProcessorMutation();

  const busy =
    isMaintenance || isPausing || isResuming || isDeleting || isReleasing || isResetting || isReissuing || isRecovering;
  const hasActiveJob = Boolean(processor.currentJobId);
  const opsStatus = processor.opsStatus ?? 'active';
  const isAzure = processor.deployment === 'azure';
  const healStuck = isProcessorHealStuck(processor);
  const automationCaption = resolveProcessorAutomationCaption(processor.lastAutomation, t);

  const canEnterMaintenance = opsStatus === 'active' && !hasActiveJob;
  const canPause = isAzure && opsStatus === 'active' && !hasActiveJob;
  const canResume = opsStatus === 'maintenance' || opsStatus === 'paused';
  const canDelete = !hasActiveJob;
  const canRelease = hasActiveJob;
  const canReset = hasActiveJob;
  const canReissue = opsStatus === 'active';
  const canRecover = opsStatus === 'active';

  const closeConfirm = () => setConfirmAction(null);

  const onEnterMaintenance = async () => {
    try {
      await enterMaintenance(processor.playerId);
      notification.notifySuccess(t('admin.processorAdmin.maintenanceEnabled', undefined, 'Maintenance mode enabled'));
      closeConfirm();
    } catch {
      notification.notifyError(
        t('admin.processorAdmin.maintenanceError', undefined, 'Failed to update maintenance mode'),
      );
    }
  };

  const onPause = async () => {
    try {
      await pause(processor.playerId);
      notification.notifySuccess(t('admin.processorAdmin.pauseSuccess', undefined, 'Processor paused'));
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.processorAdmin.pauseError', undefined, 'Failed to pause processor'));
    }
  };

  const onResume = async () => {
    try {
      await resume(processor.playerId);
      notification.notifySuccess(t('admin.processorAdmin.resumeSuccess', undefined, 'Processor resumed'));
    } catch {
      notification.notifyError(t('admin.processorAdmin.resumeError', undefined, 'Failed to resume processor'));
    }
  };

  const onReleaseLease = async () => {
    try {
      const result = await releaseLease(processor.playerId);
      const successMessage = t(
        'admin.processorAdmin.releaseLeaseSuccess',
        undefined,
        'Processor released — requeued {count} job(s)',
      ).replace('{count}', String(result.requeued));
      if (result.warnings?.length) {
        notification.notifySuccess(`${successMessage} (${result.warnings.join('; ')})`);
      } else {
        notification.notifySuccess(successMessage);
      }
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.processorAdmin.releaseLeaseError', undefined, 'Failed to release processor'));
    }
  };

  const onResetProcessor = async () => {
    try {
      const result = await resetProcessor(processor.playerId);
      const successMessage = t(
        'admin.processorAdmin.resetProcessorSuccess',
        undefined,
        'Processor reset — requeued {count} job(s)',
      ).replace('{count}', String(result.requeued));
      const suffix = !result.resetEmitted ? ' (agent offline — restart manually if needed)' : '';
      if (result.warnings?.length) {
        notification.notifySuccess(`${successMessage}${suffix} (${result.warnings.join('; ')})`);
      } else {
        notification.notifySuccess(`${successMessage}${suffix}`);
      }
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.processorAdmin.resetProcessorError', undefined, 'Failed to reset processor'));
    }
  };

  const onReissueToken = async () => {
    try {
      await reissueToken(processor.playerId);
      notification.notifySuccess(t('admin.processorAdmin.reissueSuccess', undefined, 'Token reissued'));
    } catch {
      notification.notifyError(t('admin.processorAdmin.reissueError', undefined, 'Failed to reissue token'));
    }
  };

  const onRecover = async () => {
    try {
      await recoverProcessor({ playerId: processor.playerId, mode: 'auto' });
      notification.notifySuccess(t('admin.processorAdmin.recoverSuccess', undefined, 'Processor recover started'));
    } catch {
      notification.notifyError(t('admin.processorAdmin.recoverError', undefined, 'Failed to recover processor'));
    }
  };

  const onDelete = async () => {
    try {
      await deleteProcessor(processor.playerId);
      notification.notifySuccess(t('admin.processorAdmin.deleteSuccess', undefined, 'Processor deleted'));
      closeConfirm();
    } catch {
      notification.notifyError(t('admin.processorAdmin.deleteError', undefined, 'Failed to delete processor'));
    }
  };

  const confirmPending = () => {
    switch (confirmAction) {
      case 'maintenance':
        return void onEnterMaintenance();
      case 'pause':
        return void onPause();
      case 'release':
        return void onReleaseLease();
      case 'reset':
        return void onResetProcessor();
      case 'delete':
        return void onDelete();
      default:
        return;
    }
  };

  const confirmTitle =
    confirmAction === 'maintenance'
      ? t('admin.processorAdmin.maintenanceConfirmTitle', undefined, 'Enter maintenance?')
      : confirmAction === 'pause'
        ? t('admin.processorAdmin.pauseConfirmTitle', undefined, 'Pause processor?')
        : confirmAction === 'release'
          ? t('admin.processorAdmin.releaseLeaseConfirmTitle', undefined, 'Release processor lease?')
          : confirmAction === 'reset'
            ? t('admin.processorAdmin.resetProcessorConfirmTitle', undefined, 'Reset media processor?')
            : t('admin.processorAdmin.deleteConfirmTitle', undefined, 'Delete processor?');

  const confirmText =
    confirmAction === 'maintenance'
      ? t(
          'admin.processorAdmin.maintenanceConfirmText',
          { playerId: processor.playerId },
          `Put processor ${processor.playerId} into maintenance? It will stop accepting new jobs until resumed.`,
        )
      : confirmAction === 'pause'
        ? t(
            'admin.processorAdmin.pauseConfirmText',
            { playerId: processor.playerId },
            `Pause processor ${processor.playerId}? The Azure VM will be stopped and removed from the pool until resumed.`,
          )
        : confirmAction === 'release'
          ? t(
              'admin.processorAdmin.releaseLeaseConfirmText',
              { playerId: processor.playerId },
              `Release the active lease on ${processor.playerId} and requeue the in-flight job?`,
            )
          : confirmAction === 'reset'
            ? t(
                'admin.processorAdmin.resetProcessorConfirmText',
                { playerId: processor.playerId },
                `Reset processor ${processor.playerId}? This releases any in-flight job, requeues it, and restarts the agent process on the VM.`,
              )
            : t(
                'admin.processorAdmin.deleteConfirmText',
                { playerId: processor.playerId },
                `Delete processor ${processor.playerId}? This removes the player record and any Azure VM. This cannot be undone.`,
              );

  const confirmLabel =
    confirmAction === 'maintenance'
      ? t('admin.processorAdmin.enterMaintenance', undefined, 'Enter maintenance')
      : confirmAction === 'pause'
        ? t('admin.processorAdmin.pause', undefined, 'Pause')
        : confirmAction === 'release'
          ? t('admin.processorAdmin.releaseLease', undefined, 'Release & requeue')
          : confirmAction === 'reset'
            ? t('admin.processorAdmin.resetProcessor', undefined, 'Reset processor')
            : t('admin.processorAdmin.delete', undefined, 'Delete');

  const confirmPendingBusy =
    (confirmAction === 'maintenance' && isMaintenance) ||
    (confirmAction === 'pause' && isPausing) ||
    (confirmAction === 'release' && isReleasing) ||
    (confirmAction === 'reset' && isResetting) ||
    (confirmAction === 'delete' && isDeleting);

  const confirmIsDestructive =
    confirmAction === 'delete' || confirmAction === 'reset' || confirmAction === 'release' || confirmAction === 'pause';

  return (
    <>
      <Stack spacing={0.5} alignItems="flex-start">
        <Stack direction="row" spacing={0.25} alignItems="center" flexWrap="nowrap">
          {canEnterMaintenance && (
            <Tooltip title={t('admin.processorAdmin.enterMaintenance', undefined, 'Enter maintenance')}>
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={busy}
                  onClick={() => setConfirmAction('maintenance')}
                >
                  <BuildOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canPause && (
            <Tooltip title={t('admin.processorAdmin.pause', undefined, 'Pause')}>
              <span>
                <IconButton size="small" disabled={busy} onClick={() => setConfirmAction('pause')}>
                  <PauseOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canResume && (
            <Tooltip title={t('admin.processorAdmin.resume', undefined, 'Resume')}>
              <span>
                <IconButton size="small" color="success" disabled={busy} onClick={() => void onResume()}>
                  <PlayArrowOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canRelease && (
            <Tooltip
              title={t(
                'admin.processorAdmin.releaseLeaseHint',
                undefined,
                t('admin.processorAdmin.releaseLease', undefined, 'Release & requeue stuck job'),
              )}
            >
              <span>
                <IconButton size="small" color="warning" disabled={busy} onClick={() => setConfirmAction('release')}>
                  <ReplayOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canReset && (
            <Tooltip
              title={t(
                'admin.processorAdmin.resetProcessorHint',
                undefined,
                t('admin.processorAdmin.resetProcessor', undefined, 'Reset processor'),
              )}
            >
              <span>
                <IconButton size="small" color="error" disabled={busy} onClick={() => setConfirmAction('reset')}>
                  <RestartAltOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canReissue && (
            <Tooltip title={t('admin.processorAdmin.reissueToken', undefined, 'Reissue token')}>
              <span>
                <IconButton
                  size="small"
                  color={healStuck ? 'warning' : 'default'}
                  disabled={busy}
                  onClick={() => void onReissueToken()}
                >
                  <VpnKeyOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canRecover && (
            <Tooltip title={t('admin.processorAdmin.recover', undefined, 'Recover')}>
              <span>
                <IconButton
                  size="small"
                  color={healStuck ? 'warning' : 'default'}
                  disabled={busy}
                  onClick={() => void onRecover()}
                >
                  <HealingOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip
              title={
                hasActiveJob
                  ? t('admin.processorAdmin.deleteBlockedJob', undefined, 'Release the active job first')
                  : t('admin.processorAdmin.deleteHint', undefined, 'Delete processor')
              }
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={busy || hasActiveJob}
                  onClick={() => setConfirmAction('delete')}
                >
                  <DeleteOutlined fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {busy && <CircularProgress size={16} sx={{ ml: 0.5 }} />}
        </Stack>
        {automationCaption && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {automationCaption.inFlight && <CircularProgress size={10} />}
            <Typography variant="caption" color={AUTOMATION_CAPTION_COLOR[automationCaption.severity]}>
              {automationCaption.text}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Dialog open={confirmAction !== null} onClose={closeConfirm}>
        <DialogTitle>{confirmTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>{t('common.cancel', undefined, 'Cancel')}</Button>
          <Button
            variant="contained"
            color={confirmIsDestructive ? 'error' : 'warning'}
            onClick={confirmPending}
            disabled={confirmPendingBusy}
          >
            {confirmPendingBusy ? <CircularProgress size={20} color="inherit" /> : confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
